import React, { useState, useRef, useEffect } from 'react';
import { API_BASE_URL } from '../config';

export default function AddMyKitchen() {
  const [aiDraftItems, setAiDraftItems] = useState([]);
  const [aiSource, setAiSource] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // New Dictation State
  const [showDictationModal, setShowDictationModal] = useState(false);
  const [dictationText, setDictationText] = useState('');

  const [loadingStep, setLoadingStep] = useState(0);
  const loadingSteps = [
    "Analiz ediliyor...",
    "Fiyatlar belirleniyor...",
    "Buzdolabı yerleri seçiliyor...",
    "Listeleniyor...",
    "Tamamlanıyor..."
  ];

  const [aiStatusMessage, setAiStatusMessage] = useState(null);
  const [receiptGrandTotal, setReceiptGrandTotal] = useState(0);
  const [error, setError] = useState(null);
  const [defaultZone] = useState('Fridge');
  const [defaultSubZone] = useState('Sebzeler');
  const receiptInputRef = useRef(null);
  const dictationInputRef = useRef(null);

  const API_BASE = API_BASE_URL;

  const zones = {
    Fridge: {
      name: 'Buzdolabı',
      icon: 'kitchen',
      subZones: ['Sebzeler', 'Meyveler', 'Süt Ürünleri', 'Et & Balık'],
      subZoneMapping: { 'Sebzeler': 'Vegetables', 'Meyveler': 'Fruits', 'Süt Ürünleri': 'Dairy', 'Et & Balık': 'Meat_Fish' }
    },
    Freezer: {
      name: 'Dondurucu',
      icon: 'ac_unit',
      subZones: ['Donmuş Et', 'Donmuş Sebze', 'Tatlılar'],
      subZoneMapping: { 'Donmuş Et': 'Frozen_Meat', 'Donmuş Sebze': 'Frozen_Vegetables', 'Tatlılar': 'Desserts' }
    },
    Pantry: {
      name: 'Kiler',
      icon: 'inventory',
      subZones: ['Tahıllar', 'Baharatlar', 'Konserveler'],
      subZoneMapping: { 'Tahıllar': 'Grains', 'Baharatlar': 'Spices', 'Konserveler': 'Canned' }
    },
    Snack_Cabinet: {
      name: 'Atıştırmalık Dolabı',
      icon: 'cookie',
      subZones: ['Atıştırmalıklar', 'İçecekler', 'Fırın Malzemeleri'],
      subZoneMapping: { 'Atıştırmalıklar': 'Snacks', 'İçecekler': 'Beverages', 'Fırın Malzemeleri': 'Baking' }
    }
  };

  useEffect(() => {
    let interval;
    if (aiLoading) {
      setLoadingStep(0);
      interval = setInterval(() => {
        setLoadingStep((prev) => (prev + 1) % loadingSteps.length);
      }, 3000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [aiLoading]);

  useEffect(() => {
    if (showDictationModal && dictationInputRef.current) {
      setTimeout(() => {
        dictationInputRef.current.focus();
      }, 100);
    }
  }, [showDictationModal]);

  const normalizeSubZone = (zoneKey, incomingSubZone) => {
    if (!incomingSubZone) return incomingSubZone;
    const zoneDef = zones[zoneKey];
    if (zoneDef && zoneDef.subZoneMapping) {
      const reverseEntry = Object.entries(zoneDef.subZoneMapping).find(([tr, en]) => tr === incomingSubZone || en === incomingSubZone);
      if (reverseEntry) return reverseEntry[1];
    }
    return incomingSubZone;
  };

  const normalizeAiItems = (items = []) => {
    const now = Date.now();
    return items
      .map((raw, idx) => {
        const targetZone = raw.main_zone || raw.zone || defaultZone;
        const targetSubZone = normalizeSubZone(targetZone, raw.sub_zone || raw.subzone || raw.section || defaultSubZone);

        return {
          id: raw.id || `ai-${now}-${idx}`,
          rawName: raw.rawName || raw.name || '',
          ingredient_name: raw.resolvedName || raw.name || '',
          brand: raw.brand || '',
          details: raw.details || '',
          dictionaryId: raw.dictionaryId || null,
          remaining_amount: Number(raw.amount || raw.remaining_amount || 1) || 1,
          unit: raw.unit || 'adet',
          purchase_price: raw.price || raw.purchase_price || 0,
          main_zone: targetZone,
          sub_zone: targetSubZone,
          confidence: raw._confidence ?? raw.confidence ?? 1.0,
          isNewIdentityNeeded: raw._isNewIdentityNeeded || !raw.dictionaryId,
          isConfirmed: (raw.confidence > 0.8 && raw.dictionaryId) // Auto-confirm high confidence
        };
      })
      .filter((item) => item.rawName || item.ingredient_name);
  };

  const persistToInventory = async (items) => {
    if (!items.length) return;
    for (const draft of items) {
      if (draft._isIgnored) continue;

      const payload = {
        dictionaryId: draft.dictionaryId,
        rawName: draft.rawName,
        amount: Number(draft.remaining_amount) || 1,
        unit: draft.unit || 'adet',
        price: draft.purchase_price !== '' && draft.purchase_price !== null ? Number(draft.purchase_price) : null,
        brand: draft.brand || '',
        learnAsSynonym: true // Default to true for learning loop
      };

      // Use the new confirm-mapping endpoint to trigger learning
      const endpoint = payload.dictionaryId ? '/api/ingest/confirm-mapping' : '/api/inventory';

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=utf-8'
        },
        body: JSON.stringify(payload.dictionaryId ? payload : {
          // Fallback legacy payload if no dictionaryId (should rarely happen now)
          ingredient_name: draft.ingredient_name,
          remaining_amount: payload.amount,
          unit: payload.unit,
          purchase_price: payload.price,
          main_zone: draft.main_zone,
          sub_zone: draft.sub_zone
        })
      });

      if (!response.ok) throw new Error(`${draft.ingredient_name || draft.rawName} eklenirken hata oluştu.`);
    }
  };

  const requestIngest = async ({ body, endpoint, sourceLabel, isJson = false, autoSave = false }) => {
    try {
      setAiLoading(true);
      setError(null);

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: isJson ? { 'Content-Type': 'application/json' } : {},
        body: isJson ? JSON.stringify(body) : body
      });

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload?.error || 'AI işlemi başarısız');
      }

      const normalized = normalizeAiItems(payload?.items || []);
      if (!normalized.length) {
        throw new Error('Üzgünüm, söylediğini anlayamadım. Lütfen daha net söyle.');
      }

      if (autoSave) {
        setAiStatusMessage("Doğrudan stoğa ekleniyor...");
        await persistToInventory(normalized);
        setAiStatusMessage("Başarıyla stoğa eklendi! ✅");
        setShowDictationModal(false);
        setDictationText('');
        // No need to set aiDraftItems for autosave
      } else {
        setAiDraftItems(normalized);
        setAiSource(sourceLabel || 'AI');
        setReceiptGrandTotal(payload?.grandTotal || 0);
        setAiStatusMessage(`Analiz tamamlandı. Lütfen kontrol edip onaylayın.`);
        setShowDictationModal(false);
        setDictationText('');
      }

    } catch (err) {
      console.error('Ingest Error:', err);
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setAiLoading(false);
    }
  };

  const handleDictationSubmit = (autoSave = false) => {
    if (!dictationText.trim()) return;
    requestIngest({
      body: { text: dictationText },
      endpoint: '/api/ingest/command-text',
      sourceLabel: 'Sesli Giriş',
      isJson: true,
      autoSave
    });
  };

  const handleReceiptInputChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    await requestIngest({
      body: fd,
      endpoint: '/api/ingest/receipt',
      sourceLabel: 'Görsel Tarama'
    });
    if (e.target) e.target.value = '';
  };

  const applyAiDrafts = async () => {
    try {
      setAiLoading(true);
      await persistToInventory(aiDraftItems);
      setAiDraftItems([]);
      setAiSource(null);
      setAiStatusMessage('Tüm ürünler stoğa başarıyla eklendi! ✨');
    } catch (err) {
      setError(err.message);
    } finally {
      setAiLoading(false);
    }
  };

  const removeAiDraftItem = (id) => {
    setAiDraftItems((prev) => prev.filter((item) => item.id !== id));
  };

  return (
    <div className="flex flex-col font-sans max-w-4xl mx-auto px-4 py-6">
      <input ref={receiptInputRef} type="file" accept="image/*" className="hidden" onChange={handleReceiptInputChange} />

      {/* DICTATION MODAL */}
      {showDictationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={() => setShowDictationModal(false)} />
          <div className="relative w-full max-w-md bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/10">
            <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span className="material-symbols-rounded text-blue-500">mic_none</span>
                Sesle Komut Girişi
              </h3>
              <button onClick={() => setShowDictationModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800 text-sm leading-relaxed text-blue-800 dark:text-blue-200">
                <p className="font-bold flex items-center gap-2 mb-1">
                  <span className="material-symbols-rounded text-base">tips_and_updates</span>
                  Nasıl Kullanılır?
                </p>
                Konuşmak için klavyedeki mikrofon tuşuna basın.<br />
                Örnek: <b>"3 kilo elma, 2 paket makarna, 50 tl"</b>
              </div>

              <textarea
                ref={dictationInputRef}
                value={dictationText}
                onChange={(e) => setDictationText(e.target.value)}
                className="w-full h-40 p-4 rounded-xl bg-gray-50 dark:bg-[#0d141c] border border-gray-200 dark:border-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none text-lg text-gray-900 dark:text-white placeholder:text-gray-400 transition-all font-medium"
                placeholder="Buraya konuşun veya yazın..."
              />

              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <button
                  onClick={() => handleDictationSubmit(false)}
                  disabled={!dictationText.trim() || aiLoading}
                  className="flex-1 px-4 py-3.5 rounded-xl border-2 border-blue-600 text-blue-600 dark:text-blue-400 font-bold hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all disabled:opacity-40"
                >
                  Listeye Al
                </button>
                <button
                  onClick={() => handleDictationSubmit(true)}
                  disabled={!dictationText.trim() || aiLoading}
                  className="flex-1 px-4 py-3.5 rounded-xl bg-blue-600 text-white font-bold shadow-lg shadow-blue-600/30 hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-40"
                >
                  Hemen Stoğa Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HEADER SECTION */}
      <header className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">Mutfak Girişi</h1>
            <p className="text-gray-500 dark:text-gray-400 font-medium">Fotoğraf çekin veya malzemeleri söyleyin.</p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => receiptInputRef.current?.click()}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-gray-900 dark:bg-white dark:text-gray-900 text-white font-bold shadow-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-all active:scale-95"
            >
              <span className="material-symbols-rounded">photo_camera</span>
              Fiş Oku
            </button>
            <button
              onClick={() => setShowDictationModal(true)}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-blue-600 text-white font-bold shadow-xl shadow-blue-600/20 hover:bg-blue-700 transition-all active:scale-95"
            >
              <span className="material-symbols-rounded">mic</span>
              Ses
            </button>
          </div>
        </div>

        {aiStatusMessage && !error && (
          <div className="animate-in slide-in-from-top-2 flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-2xl text-emerald-800 dark:text-emerald-300 font-semibold shadow-sm">
            <span className="material-symbols-rounded">check_circle</span>
            {aiStatusMessage}
          </div>
        )}

        {error && (
          <div className="animate-in shake flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-2xl text-red-800 dark:text-red-300 font-semibold shadow-sm">
            <span className="material-symbols-rounded">error</span>
            {error}
          </div>
        )}
      </header>

      {/* AI DRAFT LIST */}
      {aiDraftItems.length > 0 && (
        <div className="space-y-4 animate-in fade-in duration-500">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="material-symbols-rounded text-blue-500">list_alt</span>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Eklenmeyi Bekleyenler ({aiDraftItems.length})</h2>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setAiDraftItems([])} className="text-sm font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-3 py-1">Vazgeç</button>
              <button onClick={applyAiDrafts} className="bg-emerald-600 text-white text-sm font-bold px-4 py-2 rounded-xl shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 outline-none">Hepsini Onayla</button>
            </div>
          </div>

          <div className="grid gap-3">
            {aiDraftItems.map((item) => (
              <div
                key={item.id}
                className={`relative bg-white dark:bg-[#111823] border-2 rounded-2xl p-4 shadow-sm transition-all group ${item.isConfirmed
                  ? 'border-emerald-500/30 bg-emerald-50/5'
                  : item.confidence < 0.5
                    ? 'border-red-500/30 bg-red-50/5'
                    : 'border-blue-500/30 bg-blue-50/5'
                  }`}
              >
                <button onClick={() => removeAiDraftItem(item.id)} className="absolute top-3 right-3 text-gray-400 hover:text-red-500 transition-colors">
                  <span className="material-symbols-rounded">close</span>
                </button>

                <div className="flex flex-col gap-4">
                  {/* Status & Raw Name */}
                  <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${item.isConfirmed ? 'bg-emerald-500' : 'bg-orange-500'}`} />
                      <span className={item.isConfirmed ? 'text-emerald-600' : 'text-orange-600'}>
                        {item.isConfirmed ? 'Otomatik Onay' : 'Kontrol Gerekli'}
                      </span>
                      <span className="text-gray-400">|</span>
                      <span className="text-gray-500 italic lowercase font-medium">Fişteki: "{item.rawName}"</span>
                    </div>
                    {item.confidence < 0.6 && (
                      <span className="bg-red-100 dark:bg-red-900/40 text-red-600 px-2 py-0.5 rounded-full">Düşük Güven (%{Math.round(item.confidence * 100)})</span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-end">
                    {/* Ingredient Name (Resolved) */}
                    <div className="sm:col-span-5 space-y-1">
                      <label className="text-[10px] font-black text-gray-400 uppercase">Sistemdeki Karşılığı</label>
                      <div className="flex items-center gap-2">
                        <input
                          value={item.ingredient_name}
                          onChange={(e) => setAiDraftItems(prev => prev.map(i => i.id === item.id ? { ...i, ingredient_name: e.target.value } : i))}
                          className={`w-full bg-transparent text-gray-900 dark:text-white font-black outline-none border-b-2 pb-1 transition-colors ${item.isNewIdentityNeeded ? 'border-orange-500/50' : 'border-emerald-500/20 focus:border-blue-500'}`}
                        />
                      </div>
                    </div>

                    {/* Brand & Details */}
                    <div className="sm:col-span-3 space-y-1">
                      <label className="text-[10px] font-black text-gray-400 uppercase">Marka / Detay</label>
                      <input
                        value={`${item.brand} ${item.details}`.trim()}
                        readOnly
                        className="w-full bg-transparent text-[11px] text-gray-500 dark:text-gray-400 font-bold outline-none border-none py-1.5"
                      />
                    </div>

                    {/* Miktar */}
                    <div className="sm:col-span-2 space-y-1">
                      <label className="text-[10px] font-black text-gray-400 uppercase">Miktar</label>
                      <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800/50 rounded-xl px-2 py-1.5">
                        <input
                          type="number" step="0.1"
                          className="w-12 bg-transparent text-sm font-black text-gray-900 dark:text-white outline-none"
                          value={item.remaining_amount}
                          onChange={(e) => setAiDraftItems(prev => prev.map(i => i.id === item.id ? { ...i, remaining_amount: e.target.value } : i))}
                        />
                        <select
                          className="bg-transparent text-[10px] font-black text-gray-500 uppercase outline-none"
                          value={item.unit}
                          onChange={(e) => setAiDraftItems(prev => prev.map(i => i.id === item.id ? { ...i, unit: e.target.value } : i))}
                        >
                          {['adet', 'paket', 'kg', 'gr', 'lt', 'ml'].map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="sm:col-span-2 space-y-1">
                      <label className="text-[10px] font-black text-gray-400 uppercase">Fiyat</label>
                      <div className="flex items-center gap-1 bg-blue-50 dark:bg-blue-900/20 rounded-xl px-2 py-1.5 border border-blue-100/50">
                        <span className="text-blue-500 font-bold text-xs">₺</span>
                        <input
                          type="number"
                          className="w-full bg-transparent text-sm font-black text-blue-600 dark:text-blue-400 outline-none"
                          value={item.purchase_price}
                          onChange={(e) => setAiDraftItems(prev => prev.map(i => i.id === item.id ? { ...i, purchase_price: e.target.value } : i))}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* INTEGRITY SUMMARY */}
          <div className="mt-4 p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div className="space-y-0.5">
                <span className="text-[10px] font-black text-gray-400 uppercase">Tespit Edilen Toplam</span>
                <div className="text-xl font-black text-gray-900 dark:text-white">
                  {aiDraftItems.reduce((sum, i) => sum + (Number(i.purchase_price) || 0) * (Number(i.remaining_amount) || 1), 0).toFixed(2)} ₺
                </div>
              </div>

              {receiptGrandTotal > 0 && (
                <>
                  <div className="h-8 w-px bg-gray-200 dark:bg-gray-700 hidden sm:block" />
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-black text-gray-400 uppercase">Fiş Genel Toplamı</span>
                    <div className="text-xl font-black text-blue-600 dark:text-blue-400">
                      {Number(receiptGrandTotal).toFixed(2)} ₺
                    </div>
                  </div>
                </>
              )}
            </div>

            {receiptGrandTotal > 0 && Math.abs(aiDraftItems.reduce((sum, i) => sum + (Number(i.purchase_price) || 0) * (Number(i.remaining_amount) || 1), 0) - receiptGrandTotal) > 1 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 rounded-xl text-xs font-bold border border-orange-100 dark:border-orange-800 animate-pulse">
                <span className="material-symbols-rounded text-sm">warning</span>
                Tutar Uyuşmuyor! Bazı ürünler eksik olabilir.
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- LOADING MODAL --- */}
      {aiLoading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-white/70 dark:bg-black/80 backdrop-blur-xl transition-opacity animate-in fade-in" />
          <div className="relative w-full max-w-sm overflow-hidden rounded-[2.5rem] bg-white dark:bg-[#1a1a1a] shadow-2xl border border-gray-100 dark:border-gray-800 p-8 text-center animate-in zoom-in-95 fade-in">
            <div className="relative mb-8 flex justify-center">
              <div className="h-24 w-24 rounded-full border-4 border-gray-100 dark:border-gray-800" />
              <div className="absolute h-24 w-24 rounded-full border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent animate-spin" />
              <div className="absolute inset-4 rounded-full bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center">
                <span className="material-symbols-rounded text-3xl text-blue-500 animate-pulse">neurology</span>
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="text-xl font-black text-gray-900 dark:text-white transition-all duration-500">{loadingSteps[loadingStep]}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Yapay zeka mutfak için en doğrusunu hazırlıyor...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
